import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Handshake, ShieldCheck, Clock, CheckCircle, ArrowRight } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'

export default function Collaboration() {
  const { language, t, linkTo } = useLanguage()
  const navigate = useNavigate()
  const benefits = [
    {
      icon: ShieldCheck,
      title: language === 'da' ? 'Fuldt forsikret & certificeret' : 'Fully insured & certified',
      description: language === 'da'
        ? 'Vi har alle forsikringer i orden, så I kan have fuldstændig ro i sindet, når vi varetager jeres eller jeres kunders rengøring.'
        : 'We have all insurances in order, so you can have complete peace of mind when we handle your or your clients\' cleaning.'
    },
    {
      icon: Clock,
      title: language === 'da' ? 'Fleksibilitet & 24/7 dækning' : 'Flexibility & 24/7 coverage',
      description: language === 'da'
        ? 'Vi tilpasser os jeres tidsplaner og rykker ud, når det passer bedst – også uden for normal arbejdstid eller med kort varsel.'
        : 'We adapt to your schedules and respond when it fits best – also outside normal working hours or at short notice.'
    },
    {
      icon: Handshake,
      title: language === 'da' ? 'Pålidelig underleverandør' : 'Reliable subcontractor',
      description: language === 'da'
        ? 'Vi optræder professionelt og loyalt som jeres forlængede arm over for jeres kunder, og vi overholder alle aftaler til punkt og prikke.'
        : 'We act professionally and loyally as your extended arm to your clients, and we keep all agreements to the letter.'
    }
  ]

  const scrollToSection = (href) => {
    const element = document.querySelector(href)
    if (element) {
      const offset = 80
      const bodyRect = document.body.getBoundingClientRect().top
      const elementRect = element.getBoundingClientRect().top
      const elementPosition = elementRect - bodyRect
      const offsetPosition = elementPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }

  return (
    <section id="samarbejde" className="relative bg-[#F3EFE6] py-24 md:py-32 overflow-hidden">
      {/* Background Image with soft overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/134783dc-070f-44c9-81c4-8363b67f7a83.jpg"
          alt="Rolig dansk strand med klitter og marehalm"
          className="h-full w-full object-cover object-center scale-105"
        />
        {/* Soft, warm, minimalist overlay to ensure high contrast and readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#FAF8F5]/95 via-[#FAF8F5]/90 to-[#FAF8F5]/85 md:from-[#FAF8F5]/95 md:via-[#FAF8F5]/90 md:to-[#FAF8F5]/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FAF8F5] via-transparent to-[#FAF8F5]/30" />
        
        {/* Minimalist Line-Art Handshake Illustration replaced with the uploaded image */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-25 pointer-events-none select-none z-0">
          <img
            src="https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/f8c4e715-5424-4352-83ab-179316a8ec21.png"
            alt="Samarbejde og Partnerskab"
            className="w-96 h-96 md:w-[500px] md:h-[500px] lg:w-[650px] lg:h-[650px] object-contain"
            style={{
              filter: 'invert(59%) sepia(18%) saturate(1025%) hue-rotate(355deg) brightness(91%) contrast(84%)'
            }}
          />
        </div>
      </div>

      {/* Decorative lines */}
      <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#E6DFD3] to-transparent pointer-events-none z-0" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left Column - Text & CTA */}
          <div className="lg:col-span-7">
            <span className="text-xs font-semibold tracking-[0.2em] text-[#a78458] uppercase block mb-3">
              {language === 'da' ? 'Samarbejde & Partnerskab' : 'Collaboration & Partnership'}
            </span>
            <h2 className="font-marcellus text-3xl sm:text-4xl md:text-5xl font-light tracking-[0.08em] text-[#2C302E] leading-tight mb-6 uppercase">
              {language === 'da' ? (
                <>Søger I en stabil <span className="font-normal text-[#a78458]">samarbejdspartner</span> OG underleverandør?</>
              ) : (
                <>Looking for a stable <span className="font-normal text-[#a78458]">partner</span> AND subcontractor?</>
              )}
            </h2>
            <p className="text-base sm:text-lg text-[#2C302E]/80 leading-relaxed mb-8">
              {language === 'da'
                ? 'Hos Wecas ApS tror vi på langsigtede og gensidigt værdiskabende relationer. Vi samarbejder tæt med både private virksomheder, offentlige institutioner, ejendomsselskaber og andre rengøringsfirmaer, der har brug for en pålidelig underleverandør til større eller specialiserede opgaver.'
                : 'At Wecas ApS, we believe in long-term and mutually value-creating relationships. We collaborate closely with private companies, public institutions, real estate companies, and other cleaning firms that need a reliable subcontractor for larger or specialized tasks.'}
            </p>

            <div className="space-y-4 mb-10">
              <div className="flex items-start gap-3">
                <div className="mt-1 text-[#2C302E]">
                  <CheckCircle className="h-4 w-4" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#2C302E] text-sm sm:text-base uppercase tracking-wider">
                    {language === 'da' ? 'Fast samarbejdspartner' : 'Permanent partner'}
                  </h4>
                  <p className="text-xs sm:text-sm text-[#2C302E]/70">
                    {language === 'da'
                      ? 'Få en fast, skræddersyet aftale, hvor vi varetager al jeres erhvervsrengøring og byggepladsservice løbende.'
                      : 'Get a permanent, tailored agreement where we handle all your commercial cleaning and construction site services on an ongoing basis.'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-1 text-[#2C302E]">
                  <CheckCircle className="h-4 w-4" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#2C302E] text-sm sm:text-base uppercase tracking-wider">
                    {language === 'da' ? 'Professionel underleverandør' : 'Professional subcontractor'}
                  </h4>
                  <p className="text-xs sm:text-sm text-[#2C302E]/70">
                    {language === 'da'
                      ? 'Har I spidsbelastninger eller brug for specialister? Vi træder til under jeres brand med fuld loyalitet.'
                      : 'Do you have peak periods or need specialists? We step in under your brand with complete loyalty.'}
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate(linkTo('/kontakt'))}
              className="group inline-flex items-center gap-2 rounded-none bg-[#a78458] px-8 py-4 text-sm font-semibold tracking-wider text-[#FAF8F5] uppercase shadow-lg shadow-[#a78458]/20 transition-all duration-300 hover:bg-[#917047] hover:shadow-xl hover:shadow-[#a78458]/30 hover:-translate-y-1.5 hover:scale-[1.02]"
            >
              <span>{language === 'da' ? 'Bliv samarbejdspartner' : 'Become a partner'}</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>

          {/* Right Column - Benefits Cards */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-[#FAF8F5] rounded-none p-6 border border-[#E6DFD3] shadow-sm">
              <h3 className="font-marcellus text-lg font-light text-[#2C302E] mb-4 flex items-center gap-2 uppercase tracking-wider">
                {language === 'da' ? 'Hvorfor vælge Wecas?' : 'Why choose Wecas?'}
              </h3>
              <p className="text-xs text-[#2C302E]/60 uppercase tracking-wider mb-6">
                {language === 'da' ? 'Vores fundament for et stærkt partnerskab' : 'Our foundation for a strong partnership'}
              </p>

              <div className="space-y-6">
                {benefits.map((benefit, idx) => {
                  const Icon = benefit.icon
                  return (
                    <div key={idx} className="flex gap-4">
                      <div className="shrink-0 text-[#2C302E] pt-0.5">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-[#2C302E] mb-1 uppercase tracking-wider">
                          {benefit.title}
                        </h4>
                        <p className="text-xs text-[#2C302E]/70 leading-relaxed">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Quote Card */}
            <div className="bg-[#a78458] rounded-none p-6 text-[#FAF8F5] relative overflow-hidden">
              <p className="text-sm leading-relaxed mb-4 relative z-10">
                {language === 'da'
                  ? 'Vi vægter stabilitet og åben dialog utrolig højt. Et samarbejde med os er baseret på gensidig tillid og stærke faglige standarder.'
                  : 'We value stability and open dialogue incredibly highly. A collaboration with us is based on mutual trust and strong professional standards.'}
              </p>
              <p className="text-xs font-semibold tracking-wider uppercase relative z-10">
                {language === 'da' ? '– Ledelsen, Wecas ApS' : '– Management, Wecas ApS'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
